import React from 'react';
import { formatPhoneNumber } from '../../utils/formatters';
import { getJobTypeCommission } from '../../utils/jobTypeCommission';

const DetailInformationTab = ({
  status, setStatus,
  customer, setCustomer,
  phone, setPhone,
  email, setEmail,
  territory, setTerritory,
  type, setType,
  address, setAddress,
  customerAddresses,
  franchises,
  projectTypes = [],
  projectSource, setProjectSource,
  projectSources,
  pndDate, setPndDate,
  estimateDate, setEstimateDate,
  startDate, setStartDate,
  completionDate, setCompletionDate,
  preTaxValue, setPreTaxValue,
  preTaxPlaceholder, setPreTaxPlaceholder,
  paymentType, setPaymentType,
  claimNumber, setClaimNumber,
  invoiceNumber, setInvoiceNumber,
  invoiceSentDate, setInvoiceSentDate,
  paidInFull, setPaidInFull,
  mgmtBonusPaid, setMgmtBonusPaid,
  projectPaidDate, setProjectPaidDate,
  restorationCorp, setRestorationCorp,
  nonCommission, setNonCommission,
  authSigned, setAuthSigned,
  uploadRequired, setUploadRequired,
  typeCommissionMap = {},
}) => {
  const visibleProjectSources = projectSource && !projectSources.includes(projectSource)
    ? [...projectSources, projectSource].sort((a, b) => a.localeCompare(b))
    : projectSources;
  const visibleProjectTypes = type && !projectTypes.includes(type)
    ? [...projectTypes, type].sort((a, b) => a.localeCompare(b))
    : projectTypes;
  const typeCommission = getJobTypeCommission(typeCommissionMap, type);
  const isLegacyProjectType = Boolean(type && !projectTypes.includes(type));

  return (
    <div className="bg-white rounded shadow p-6">
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-xl text-gray-700">Project Information</h2>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Status:</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-gray-300 rounded px-3 py-1">
            <option>Accounts Receivable</option><option>Check-In</option><option>Closed</option><option>Completed</option><option>Dead</option>
            <option>Estimate</option><option>In Progress</option><option>On-Hold</option><option>Scheduled</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-8">
        <div>
          <h3 className="text-gray-500 italic mb-4">Basics</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-red-500 text-sm w-32 text-right">Customer:</label>
              <input type="text" value={customer} onChange={(e) => setCustomer(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="customer-input" />
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm w-32 text-right">Phone:</label>
              <input type="text" value={phone} onChange={(e) => setPhone(formatPhoneNumber(e.target.value))} className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="phone-input" />
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm w-32 text-right">Email:</label>
              <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="email-input" />
            </div>
            <div className="flex items-center gap-4">
              <label className="text-red-500 text-sm w-32 text-right">Territory:</label>
              <select value={territory} onChange={(e) => setTerritory(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1">
                <option value="">-- select --</option>
                {(franchises || []).map((f, i) => {
                  const val = `${f.name} (${f.number})`;
                  return <option key={i} value={val}>{val}</option>;
                })}
                {territory && !(franchises || []).some(f => `${f.name} (${f.number})` === territory) && (
                  <option value={territory}>{territory}</option>
                )}
              </select>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-red-500 text-sm w-32 text-right">Type:</label>
              <select value={type} onChange={(e) => setType(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="project-type-select">
                <option value="">-- select --</option>
                {visibleProjectTypes.map(typeName => <option key={typeName} value={typeName}>{typeName}</option>)}
              </select>
            </div>
            {isLegacyProjectType && (
              <div className="flex items-start gap-4" data-testid="legacy-job-type-notice">
                <span className="w-32"></span>
                <p className="text-xs text-amber-700 flex-1">This legacy Job Type is no longer listed in Settings. Select a current Job Type and save to update this project.</p>
              </div>
            )}
            {type && typeCommission !== undefined && (
              <div className="flex items-start gap-4" data-testid="type-commission-hint">
                <span className="w-32"></span>
                {nonCommission ? (
                  <p className="text-xs text-gray-500 italic flex-1">
                    Non-Commission Project is checked — commission for <strong>{type}</strong> is not applied to Expenses.
                  </p>
                ) : typeCommission > 0 ? (
                  <p className="text-xs text-[#5B8DB8] flex-1">
                    <strong>{type}</strong> adds a <strong>{typeCommission}%</strong> commission to Expenses.
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 italic flex-1">
                    <strong>{type}</strong> has no commission rate configured.
                  </p>
                )}
              </div>
            )}
            <div className="flex items-center gap-4">
              <label className="text-red-500 text-sm w-32 text-right">Address:</label>
              {customerAddresses && customerAddresses.length > 0 ? (
                <select value={address} onChange={(e) => setAddress(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="address-select">
                  <option value="">-- select --</option>
                  {customerAddresses.map((addr, index) => {
                    const fullAddress = `${addr.street_address || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zip || ''}`.trim();
                    return (
                      <option key={index} value={fullAddress}>{fullAddress}</option>
                    );
                  })}
                  {address && !customerAddresses.some(a => `${a.street_address || ''}, ${a.city || ''}, ${a.state || ''} ${a.zip || ''}`.trim() === address) && (
                    <option value={address}>{address}</option>
                  )}
                </select>
              ) : (
                <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" />
              )}
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm w-32 text-right">Project Source:</label>
              <select value={projectSource} onChange={(e) => setProjectSource(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" data-testid="project-source-select">
                <option value="">-- select --</option>
                {visibleProjectSources.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <h3 className="text-gray-500 italic mb-4">Key Dates</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><label className="text-red-500 text-sm w-32 text-right">FNOL Date:</label><input type="date" value={pndDate} onChange={(e) => setPndDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" /></div>
            <div className="flex items-center gap-4"><label className="text-sm w-32 text-right">Estimate Date:</label><input type="date" value={estimateDate} onChange={(e) => setEstimateDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" /></div>
            <div className="flex items-center gap-4"><label className="text-sm w-32 text-right">Start Date:</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" /></div>
            <div className="flex items-center gap-4"><label className={`text-sm w-32 text-right ${['Accounts Receivable', 'Closed', 'Completed'].includes(status) ? 'text-red-500' : ''}`}>Completion Date:</label><input type="date" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} className={`border rounded px-3 py-2 flex-1 ${['Accounts Receivable', 'Closed', 'Completed'].includes(status) && !completionDate ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} /></div>
          </div>
        </div>
        <div>
          <h3 className="text-gray-500 italic mb-4">Financial</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Pre-Tax Total Value ($):</label>
              <input type="text" value={preTaxValue} onChange={(e) => setPreTaxValue(e.target.value)} onBlur={(e) => {
                const raw = String(e.target.value).trim();
                if (!raw) return;
                const num = parseFloat(raw.replace(/[$,]/g, ''));
                if (!Number.isNaN(num)) setPreTaxValue(num.toFixed(2));
              }} className="border border-gray-300 rounded px-3 py-2 w-32" data-testid="pre-tax-value" />
              <label className="flex items-center gap-1 text-sm text-gray-600"><input type="checkbox" checked={preTaxPlaceholder} onChange={(e) => setPreTaxPlaceholder(e.target.checked)} className="w-4 h-4" />Placeholder</label>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Payment Type:</label>
              <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1">
                <option value="">-- select --</option><option>Claim</option><option>COI</option><option>Direct Pay</option>
              </select>
            </div>
            <div className="flex items-center gap-4"><label className="text-sm w-40 text-right">Claim Number:</label><input type="text" value={claimNumber} onChange={(e) => setClaimNumber(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" /></div>
            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Invoice Number:</label>
              <div className="flex items-center gap-3 flex-1">
                <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className="border border-gray-300 rounded px-3 py-2 flex-1" />
                <label className="flex items-center gap-1 text-sm text-gray-600 whitespace-nowrap"><input type="checkbox" checked={paidInFull} onChange={(e) => setPaidInFull(e.target.checked)} className="w-4 h-4" />Paid in Full</label>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Invoice Sent Date:</label>
              <input type="date" value={invoiceSentDate} onChange={(e) => setInvoiceSentDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-36" data-testid="invoice-sent-date-input" />
              <span className="text-[#5B9BD5] cursor-help" title="The date the invoice was initially sent to the customer. Drives the >60/>90 day aging buckets on the Accounts Receivable page.">&#9432;</span>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Mgmt Bonus Paid:</label>
              <input type="date" value={mgmtBonusPaid} onChange={(e) => setMgmtBonusPaid(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-36" data-testid="paid-date-input" />
              <span className="text-[#5B9BD5] cursor-help" title="Management bonus paid amount">&#9432;</span>
            </div>
            <div className="flex items-center gap-4">
              <label className={`text-sm w-40 text-right ${(status === 'Closed' || paidInFull) ? 'text-red-500' : ''}`}>Project Paid Date:</label>
              <input type="date" value={projectPaidDate} onChange={(e) => setProjectPaidDate(e.target.value)} className={`border rounded px-3 py-2 w-36 ${(status === 'Closed' || paidInFull) && !projectPaidDate ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} data-testid="project-paid-date-input" />
            </div>
          </div>
          <h3 className="text-gray-500 italic mt-8 mb-4">Project Options</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Non-Commission Project:</label>
              <input type="checkbox" checked={nonCommission} onChange={(e) => setNonCommission(e.target.checked)} className="w-4 h-4" />
              <span className="text-[#5B9BD5] cursor-help" title="Check if this project should not generate commissions">&#9432;</span>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Authorization Signed:</label>
              <input type="checkbox" checked={authSigned} onChange={(e) => setAuthSigned(e.target.checked)} className="w-4 h-4" />
              <span className="text-[#5B9BD5] cursor-help" title="Check if authorization has been signed">&#9432;</span>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm w-40 text-right">Upload Required:</label>
              <input type="checkbox" checked={uploadRequired} onChange={(e) => setUploadRequired(e.target.checked)} className="w-4 h-4" />
              <span className="text-[#5B9BD5] cursor-help" title="Check if documents need to be uploaded">&#9432;</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailInformationTab;
